import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared dialog behavior: initial focus, Tab trap, Escape-to-close,
 * and focus restore to the trigger on close.
 *
 * The container element should have tabIndex={-1} so it can take
 * initial focus when it contains no focusable children.
 */
export function useModalA11y(isOpen, onClose, containerRef) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const restoreTarget = document.activeElement;
    const container = containerRef.current;
    const firstFocusable = container?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable || container)?.focus?.();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !containerRef.current) return;

      const focusables = [...containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (el) => el.getClientRects().length > 0 || el === document.activeElement
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!containerRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      restoreTarget?.focus?.();
    };
  }, [isOpen, containerRef]);
}

export default useModalA11y;
